/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import { LegalPage, Section, SubSection, BulletList } from "../components/LegalPage";

export default function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      updated="26 February 2026"
      intro="This Privacy Policy explains what information ShouldIBuyIt.co.uk (the 'Site', 'we', 'us') collects when you use the website, how that information is used, the lawful basis for processing it under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018, and the rights you have as a user."
    >
      <Section title="1. Who we are">
        <p>
          ShouldIBuyIt.co.uk is an AI-powered shopping recommendation service operated from the United Kingdom.
          For any privacy-related question, including the rights described in section 9 below, please contact us at{" "}
          <a className="text-emerald-500 underline" href="mailto:hello@shouldibuyit.co.uk">hello@shouldibuyit.co.uk</a>.
        </p>
      </Section>

      <Section title="2. What information we collect">
        <SubSection title="2.1 Information you provide">
          <BulletList items={[
            "Search queries and chat messages you send to the AI assistant.",
            "Optional feedback (helpful / not helpful votes, 'did you buy it?' responses).",
            "Information you provide if you contact us directly by email.",
          ]} />
        </SubSection>
        <SubSection title="2.2 Information collected automatically">
          <BulletList items={[
            "Anonymous chat session identifiers used to maintain a conversation across follow-up messages.",
            "A privacy-preserving voter fingerprint (a non-reversible SHA-256 hash of your IP address combined with your browser's user-agent string) used only to count unique voters towards public approval metrics. The raw IP address and user-agent are NEVER stored.",
            "Basic technical request data (HTTP method, URL, timestamp, response status) retained briefly in server logs for security and debugging.",
            "Aggregate affiliate-click events (which retailer link was clicked, which category, when) used to measure performance. These events are not linked to any individual user.",
          ]} />
        </SubSection>
        <SubSection title="2.3 Information stored on your device">
          <p>
            We use local storage in your browser (not cookies) to remember your theme preference, your recent
            search history (which is visible only to you on your own device) and, for administrators, an
            authentication token. None of this is transmitted to a third party. See our{" "}
            <Link to="/cookies" className="text-emerald-500 underline">Cookie Policy</Link> for the full list.
          </p>
        </SubSection>
      </Section>

      <Section title="3. How we use your information">
        <BulletList items={[
          "To generate a buying recommendation in response to your question.",
          "To maintain the context of a conversation if you ask a follow-up question.",
          "To improve recommendation quality over time (e.g. learning that certain queries are commonly downvoted).",
          "To count unique voters for public approval percentages, using only the non-reversible hash described in section 2.2.",
          "To track affiliate clicks in aggregate so we can report retailer performance.",
          "To respond to enquiries you send by email.",
        ]} />
      </Section>

      <Section title="4. Lawful basis for processing">
        <p>We rely on the following lawful bases under Article 6 UK GDPR:</p>
        <BulletList items={[
          "Legitimate interests (Art. 6(1)(f)) for processing search queries, chat messages and aggregate analytics so we can provide and improve the recommendation service.",
          "Consent (Art. 6(1)(a)) for any optional analytics or marketing cookies — you decide via the cookie banner.",
          "Contract / pre-contractual steps (Art. 6(1)(b)) where you choose to contact us by email about a service.",
          "Legal obligation (Art. 6(1)(c)) where retention is required by law (for example, tax or accounting records).",
        ]} />
      </Section>

      <Section title="5. AI processing">
        <p>
          When you submit a query, the text of that query is sent to our AI model provider (Anthropic, Inc.) to
          generate a response. The provider processes your query only to produce that response and does not use
          it to train their models. No personally identifying information is required to use the service — please
          do not include personal data (names, email addresses, financial details, health information) in your queries.
          See our <Link to="/ai-disclaimer" className="text-emerald-500 underline">AI Disclaimer</Link> for the limits of AI-generated recommendations.
        </p>
      </Section>

      <Section title="6. Affiliate links">
        <p>
          When you click a "Find on Amazon" or "Buy on Amazon" button, you are taken to Amazon's UK site. Amazon
          may set its own cookies and process your data under its own privacy policy. As an Amazon Associate we
          earn a commission from qualifying purchases at no extra cost to you. The commission does not influence
          the recommendation. See our <Link to="/affiliate-disclosure" className="text-emerald-500 underline">Affiliate Disclosure</Link> for full details.
        </p>
      </Section>

      <Section title="7. Third-party services">
        <BulletList items={[
          "Anthropic (claude.ai/api) — AI model that generates recommendations from your queries.",
          "Amazon UK — destination of all 'Find on Amazon' affiliate links.",
          "Wikipedia — public read-only API used to look up royalty-free product images. No user data is sent.",
          "Our cloud hosting provider — processes requests and stores the database. No third-party analytics scripts are loaded without your consent.",
        ]} />
      </Section>

      <Section title="8. Data retention">
        <BulletList items={[
          "Search queries and chat messages: retained for up to 24 months for quality improvement, then permanently deleted or anonymised.",
          "Affiliate click events: retained as aggregate counts indefinitely; no per-user records.",
          "Voter fingerprints (hashes): retained as long as the vote remains relevant to the recommendation it relates to.",
          "Email enquiries: retained for up to 24 months after the last reply, then deleted.",
        ]} />
      </Section>

      <Section title="9. Your rights under UK GDPR">
        <p>You have the right to:</p>
        <BulletList items={[
          "Access the personal data we hold about you (Art. 15).",
          "Have inaccurate personal data corrected (Art. 16).",
          "Have your personal data erased where the lawful basis no longer applies (Art. 17).",
          "Restrict or object to processing based on legitimate interests (Art. 18 & 21).",
          "Withdraw consent at any time for processing based on consent (Art. 7(3)) — including reopening the cookie banner from the footer.",
          "Receive a portable copy of personal data you provided to us (Art. 20).",
          "Lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk if you believe your rights have been infringed.",
        ]} />
        <p>
          To exercise any of these rights, email{" "}
          <a className="text-emerald-500 underline" href="mailto:hello@shouldibuyit.co.uk">hello@shouldibuyit.co.uk</a>.
          We will respond within one calendar month.
        </p>
      </Section>

      <Section title="10. Children's privacy">
        <p>
          The Site is intended for adults aged 18 or over. We do not knowingly collect data from children. If you
          believe a child has provided us with personal data, please contact us and we will delete it.
        </p>
      </Section>

      <Section title="11. Data security">
        <p>
          We use HTTPS in transit, encryption-at-rest where supported by our hosting provider, scoped admin
          authentication, and limit access to production data to a small number of authorised individuals.
          No system can be 100% secure; if we become aware of a breach affecting your data we will notify
          the ICO within 72 hours where the law requires it and contact affected users where appropriate.
        </p>
      </Section>

      <Section title="12. International transfers">
        <p>
          Some third-party services we use (notably Anthropic and Amazon) may process data outside the UK and
          EEA. Where data is transferred, we rely on the UK adequacy regulations, the UK International Data
          Transfer Agreement, or Standard Contractual Clauses with the appropriate UK Addendum.
        </p>
      </Section>

      <Section title="13. Changes to this policy">
        <p>
          We may update this policy from time to time. The "Last updated" date at the top of the page will
          always reflect the most recent change. Material changes will be highlighted in the cookie banner the
          next time you visit.
        </p>
      </Section>

      <Section title="14. Contact">
        <p>
          Email: <a className="text-emerald-500 underline" href="mailto:hello@shouldibuyit.co.uk">hello@shouldibuyit.co.uk</a>
        </p>
      </Section>
    </LegalPage>
  );
}
